import { makeAutoObservable, runInAction } from "mobx";
import { IManagerCredentials, ManagerCredentialsStore, ManagersListStore } from "./manager-credentials-stores";


export interface ICollectCredentialsOptions {
    validate?: boolean; /** when true will validate credentials on server before returning to caller */
}

const DefaultCollectCredentialsOptions: ICollectCredentialsOptions = {
    validate: true,
}

enum REJECT_REASON {
    ABORTED = 'ABORTED',
}

interface ICollectCredentialsResult {
    credentials?: IManagerCredentials;
    reject_reason?: REJECT_REASON;
}


class ManagerApprovalModalStore {
    private _componentRegistered: boolean = false;
    private _managersList: ManagersListStore = new ManagersListStore();
    private _request?: { 
        promise: Promise<ICollectCredentialsResult>,
        resolve?: (value: ICollectCredentialsResult | PromiseLike<ICollectCredentialsResult>) => void,
        credentialsStore: ManagerCredentialsStore,
        options: ICollectCredentialsOptions,
    };

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    public get credentialsStore(): ManagerCredentialsStore|undefined { return this._request?.credentialsStore; }
    public get managersList(): ManagersListStore { return this._managersList; }

    public registerComponent(): void {
        if (this._componentRegistered) {
            throw new Error('<ManagerApprovalModal /> GLOBAL COMPONENT: do not add it to any page, please use managerApprovalModalStore only');
        }
        this._componentRegistered = true;
    }
    public unregisterComponent(): void {
        this._componentRegistered = false;
    }

    /** returns promise that is never rejected, it can only be resolved, when used no need to wrap in try catch */
    public async collectCredentials(options?: ICollectCredentialsOptions): Promise<ICollectCredentialsResult> {
        /** when modal is already open and in process of collecting credentials - return same promise */
        if (this._request) {
            return this._request.promise;
        }

        const opts: ICollectCredentialsOptions = { ...DefaultCollectCredentialsOptions, ...options };
        this._managersList.load();

        /** construction of the promise object */
        let _resolve;
        this._request = { 
            promise: new Promise<ICollectCredentialsResult>((resolve) => {
                _resolve = resolve;
            }),
            credentialsStore: new ManagerCredentialsStore(this._managersList),
            options: opts,
        };
        this._request.resolve = _resolve;

        return this._request.promise;
    }

    private _ensureRequest(): void {
        if (!this._request) {
            /** if you got here - something is wrong with _request management, in all places this method is used _request must exist */
            throw new Error('_request does not exists, should never happen');
        }
    }

    public close(): void {
        this._ensureRequest();
        this._request!.resolve!({
            reject_reason: REJECT_REASON.ABORTED
        });
        this._request = undefined;
    }

    public async submit() {
        this._ensureRequest();    

        let isValidated = true;
        if (this._request!.options.validate) {
            isValidated = await this.credentialsStore!.validate();
        }
    
        /** during validation of credentials the modal could be closed by merchant - hence the need to check if promise exists */
        if (isValidated && this._request) {
            runInAction(() => {
                this._request!.resolve!({
                    credentials: this.credentialsStore!.getCredentials(),
                });
                this._request = undefined;
            });
        }
    }

}



export default new ManagerApprovalModalStore();
